import {
  CODIGOS_ERRO,
  MENSAGEM_POR_CODIGO,
  type CodigoErro,
  type DetalheErro,
  type RespostaErro,
} from '@baiocchi/shared'

/**
 * Erro de API já interpretado.
 *
 * O **código** é o contrato estável (`03-endpoints-draft.md §14`); a mensagem já
 * vem em pt-BR do servidor e é ela que a tela mostra. A web não reescreve a regra
 * de negócio em texto próprio: duas redações da mesma regra divergem no primeiro
 * ajuste, e a do servidor é a que corresponde ao que de fato aconteceu.
 */
export class ErroApi extends Error {
  readonly status: number
  readonly codigo: CodigoErro | 'ERRO_DE_REDE'
  readonly detalhes: ReadonlyArray<DetalheErro>
  readonly requestId: string | null

  constructor(entrada: {
    status: number
    codigo: CodigoErro | 'ERRO_DE_REDE'
    mensagem: string
    detalhes?: ReadonlyArray<DetalheErro>
    requestId?: string | null
  }) {
    super(entrada.mensagem)
    this.name = 'ErroApi'
    this.status = entrada.status
    this.codigo = entrada.codigo
    this.detalhes = entrada.detalhes ?? []
    this.requestId = entrada.requestId ?? null
  }

  /** `true` quando o problema é o papel do usuário, não o dado enviado. */
  get semPermissao(): boolean {
    return this.codigo === CODIGOS_ERRO.SEM_PERMISSAO
  }

  /**
   * Recurso inexistente **ou fora do escopo** — a API responde `404` nos dois
   * casos de propósito (RNF-24), e a tela não tem como (nem deve) distinguir.
   */
  get naoEncontrado(): boolean {
    return this.status === 404
  }

  get sessaoInvalida(): boolean {
    return (
      this.codigo === CODIGOS_ERRO.SESSAO_EXPIRADA ||
      this.codigo === CODIGOS_ERRO.REFRESH_REUSADO ||
      this.codigo === CODIGOS_ERRO.CREDENCIAIS_INVALIDAS
    )
  }

  get exigeTrocaDeSenha(): boolean {
    return this.codigo === CODIGOS_ERRO.TROCA_SENHA_OBRIGATORIA
  }

  /** Endpoint previsto no contrato mas ainda não implementado na API. */
  get naoImplementado(): boolean {
    return this.status === 501 || this.status === 404
  }

  /** Erros por campo, no formato que os formulários consomem. */
  errosPorCampo(): Record<string, string> {
    const mapa: Record<string, string> = {}
    for (const detalhe of this.detalhes) {
      if (detalhe.campo && !mapa[detalhe.campo]) mapa[detalhe.campo] = detalhe.mensagem
    }
    return mapa
  }
}

/** `true` quando o corpo tem a forma do envelope de erro da API (§9). */
function ehEnvelopeDeErro(valor: unknown): valor is RespostaErro {
  if (typeof valor !== 'object' || valor === null) return false
  const erro = (valor as { erro?: unknown }).erro
  return (
    typeof erro === 'object' &&
    erro !== null &&
    typeof (erro as { codigo?: unknown }).codigo === 'string'
  )
}

export async function interpretarErro(resposta: Response): Promise<ErroApi> {
  let corpo: unknown = null
  try {
    corpo = await resposta.json()
  } catch {
    corpo = null
  }

  if (ehEnvelopeDeErro(corpo)) {
    return new ErroApi({
      status: resposta.status,
      codigo: corpo.erro.codigo,
      mensagem: corpo.erro.mensagem,
      detalhes: corpo.erro.detalhes,
      requestId: corpo.erro.requestId,
    })
  }

  // Fora do envelope: proxy, gateway ou rota inexistente. Não inventa código de
  // domínio — usa o genérico, que é o que o catálogo prevê.
  return new ErroApi({
    status: resposta.status,
    codigo:
      resposta.status === 404
        ? CODIGOS_ERRO.RECURSO_NAO_ENCONTRADO
        : resposta.status === 429
          ? CODIGOS_ERRO.LIMITE_REQUISICOES
          : CODIGOS_ERRO.ERRO_INTERNO,
    mensagem: MENSAGEM_POR_CODIGO[
      resposta.status === 404
        ? CODIGOS_ERRO.RECURSO_NAO_ENCONTRADO
        : resposta.status === 429
          ? CODIGOS_ERRO.LIMITE_REQUISICOES
          : CODIGOS_ERRO.ERRO_INTERNO
    ],
    requestId: resposta.headers.get('x-request-id'),
  })
}

export function erroDeRede(causa: unknown): ErroApi {
  return new ErroApi({
    status: 0,
    codigo: 'ERRO_DE_REDE',
    mensagem:
      'Não foi possível falar com o servidor. Verifique a conexão e tente novamente.',
    detalhes: [
      { campo: '', codigo: 'CAUSA', mensagem: causa instanceof Error ? causa.message : String(causa) },
    ],
  })
}

/** Mensagem exibível de qualquer erro capturado por `react-query` ou `try`. */
export function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message
  if (erro instanceof Error) return erro.message
  return MENSAGEM_POR_CODIGO[CODIGOS_ERRO.ERRO_INTERNO]
}
